
import Vue from "vue";
import {
  AssessmentQuestion,
  CandidateAiAssessmentsViewOptions
} from "@/interfaces/candidate/canidate_assessments";
import { TranslateResult } from "vue-i18n";
import { minLength, required } from "vuelidate/lib/validators";

export default Vue.extend({
  name: "QuestionComponent",
  props: {
    question: {
      type: Object as () => AssessmentQuestion,
      required: true
    },
    questionIndex: {
      type: Number,
      required: true
    },
    isLastQuestion: {
      type: Boolean,
      default: false
    },
    submitting: {
      type: Boolean,
      default: false
    },
    currentAnswer: {
      type: String,
      default: ""
    }
  },
  beforeDestroy() {
    if (this.timerId) clearInterval(this.timerId);
  },
  data() {
    return {
      selectedAnswer: "",
      CandidateAiAssessmentsViewOptions,
      remainingSeconds: 0 as number,
      timerId: null as ReturnType<typeof setInterval> | null
    };
  },
  computed: {
    requiredError(): TranslateResult[] {
      const errors: TranslateResult[] = [];
      if (!this.$v.selectedAnswer?.$dirty) return errors;
      !this.$v.selectedAnswer?.required &&
        errors.push(this.$t("rules.required"));
      !this.$v.selectedAnswer?.minLength &&
        errors.push(this.$t("rules.minLength", { min: 4 }));

      return errors;
    }
  },
  watch: {
    question: {
      handler() {
        // Set the answer from currentAnswer prop if available, otherwise clear it
        this.selectedAnswer = this.currentAnswer || "";
        this.startTimer();
      },
      immediate: true
    },
    currentAnswer: {
      handler(newAnswer) {
        // Update selectedAnswer when currentAnswer prop changes
        this.selectedAnswer = newAnswer || "";
      },
      immediate: true
    },
    submitting: {
      handler(newVal) {
        if (!newVal) {
          // Reset validation state when not submitting
          this.$v.$reset();
        }
      },
      immediate: true
    }
  },
  // Form validations
  validations() {
    return {
      selectedAnswer: {
        required,
        minLength: minLength(4)
      }
    };
  },
  methods: {
    getQuestionTypeColor(
      questionType: CandidateAiAssessmentsViewOptions
    ): string {
      const colors = {
        [CandidateAiAssessmentsViewOptions.MULTIPLE_CHOICE]: "primary",
        [CandidateAiAssessmentsViewOptions.SHORT_ANSWER]: "primary",
        [CandidateAiAssessmentsViewOptions.TRUE_FALSE]: "primary"
      };
      return colors[questionType] || "primary";
    },
    isArabic() {
      return localStorage.getItem("locale") === "ar";
    },
    getQuestionTypeIcon(
      questionType: CandidateAiAssessmentsViewOptions
    ): string {
      const icons = {
        [CandidateAiAssessmentsViewOptions.MULTIPLE_CHOICE]:
          "mdi-checkbox-multiple-marked",
        [CandidateAiAssessmentsViewOptions.SHORT_ANSWER]: "mdi-text",
        [CandidateAiAssessmentsViewOptions.TRUE_FALSE]: "mdi-check-circle"
      };
      return icons[questionType] || "mdi-help-circle";
    },
    formatQuestionType(
      questionType: CandidateAiAssessmentsViewOptions
    ): string {
      const labels = {
        [CandidateAiAssessmentsViewOptions.MULTIPLE_CHOICE]: this.$t(
          "ai-assessments.header.multiple-choice"
        ),
        [CandidateAiAssessmentsViewOptions.SHORT_ANSWER]: this.$t(
          "ai-assessments.header.short-answer"
        ),
        [CandidateAiAssessmentsViewOptions.TRUE_FALSE]: this.$t(
          "ai-assessments.header.true-false"
        )
      };
      return (
        labels[questionType]?.toString() ||
        this.$t("ai-assessments.question.unknown").toString()
      );
    },
    submitAnswer(ignoreValidation: any) {
      if (typeof ignoreValidation !== "boolean" || !ignoreValidation) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      }

      this.$emit("answer-submitted", {
        questionId: this.question.id,
        answer: this.selectedAnswer,
        questionType: this.question.question_type
      });
    },
    startTimer() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
      const allowed = Number(this.question.question_time_allowed || 0);
      this.remainingSeconds = allowed > 0 ? allowed : 0;
      if (this.remainingSeconds <= 0) return;
      this.timerId = setInterval(() => {
        if (this.remainingSeconds > 0) this.remainingSeconds -= 1;
        if (this.remainingSeconds <= 0) {
          if (this.timerId) {
            clearInterval(this.timerId);
            this.timerId = null;
          }
          // Auto-submit and move to next when time is up
          this.submitAnswer(true);
        }
      }, 1000);
    }
  }
});
